import React, { useEffect, useRef } from 'react';
import { Card } from 'antd';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import PropTypes from 'prop-types';

import { $createListItemNode, $createListNode, ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { OverflowNode } from '@lexical/overflow';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import ToolbarPlugin from '../../../../components/notes-editor/plugins/ToolbarPlugin';
import theme from '../../../../components/notes-editor/theme';

import './controlled-editor-styles.scss';

// Shared ref to track if we're currently loading content (not user input)
// This needs to be outside the component so OnChangeHandler can access it
let isLoadingContent = false;

/**
 * Parse template text with bullet points (·) and create appropriate Lexical nodes
 */
function createNodesFromTemplate(template) {
    const lines = template.split('\n');
    const nodes = [];
    let currentList = null;

    lines.forEach((line) => {
        if (line.startsWith('· ')) {
            if (!currentList) {
                currentList = $createListNode('bullet');
                nodes.push(currentList);
            }
            const listItem = $createListItemNode();
            const text = $createTextNode(line.slice(2));
            listItem.append(text);
            currentList.append(listItem);
        } else {
            if (currentList) {
                currentList = null;
            }
            const paragraph = $createParagraphNode();
            const text = $createTextNode(line);
            paragraph.append(text);
            nodes.push(paragraph);
        }
    });

    return nodes;
}

/**
 * Plugin that syncs external content prop to the editor state.
 * Sets isLoadingContent flag to prevent onChange from firing during load.
 */
function ContentSyncPlugin({ content, contentVersion }) {
    const [editor] = useLexicalComposerContext();
    const lastVersionRef = useRef(contentVersion);

    useEffect(() => {
        // Only update if contentVersion changed (meaning external content changed)
        if (contentVersion === lastVersionRef.current) {
            return;
        }

        lastVersionRef.current = contentVersion;

        // Set flag to indicate we're loading content - not user input
        isLoadingContent = true;

        if (!content) {
            // Empty content - add empty paragraph
            editor.update(
                () => {
                    const root = $getRoot();
                    root.clear();
                    const paragraph = $createParagraphNode();
                    root.append(paragraph);
                },
                { tag: 'history-merge' }
            );

            // Reset flag after update completes
            setTimeout(() => {
                isLoadingContent = false;
            }, 100);

            return;
        }

        // Try to parse as JSON (Lexical editor state)
        let isValidJson = false;
        try {
            JSON.parse(content);
            isValidJson = true;
        } catch {
            isValidJson = false;
        }

        if (isValidJson) {
            // It's a valid Lexical state - use setEditorState for proper restoration
            try {
                const editorState = editor.parseEditorState(content);
                editor.setEditorState(editorState, { tag: 'history-merge' });
            } catch (e) {
                console.warn('[ControlledLexicalEditor] Failed to parse editor state:', e);
            }
        } else {
            // Plain text - parse for bullet points and create appropriate nodes
            editor.update(
                () => {
                    const root = $getRoot();
                    root.clear();
                    const nodes = createNodesFromTemplate(content);
                    nodes.forEach((node) => {
                        root.append(node);
                    });
                },
                { tag: 'history-merge' }
            );
        }

        // Reset flag after update completes
        setTimeout(() => {
            isLoadingContent = false;
        }, 100);
    }, [editor, content, contentVersion]);

    return null;
}

ContentSyncPlugin.propTypes = {
    content: PropTypes.string,
    contentVersion: PropTypes.number
};

/**
 * Plugin that handles onChange events, but only for user input (not programmatic updates)
 */
function UserInputChangePlugin({ onChange }) {
    const [editor] = useLexicalComposerContext();
    const lastContentRef = useRef('');

    useEffect(() => {
        const removeListener = editor.registerUpdateListener(({ editorState, tags }) => {
            // Skip if this is a programmatic update (loading content)
            if (isLoadingContent) {
                return;
            }

            // Skip if this update was tagged as history-merge (programmatic)
            if (tags.has('history-merge')) {
                return;
            }

            const json = editorState.toJSON();
            let newContent = '';

            if (json?.root?.direction) {
                newContent = JSON.stringify(json);
            }

            // Only call onChange if content actually changed
            if (newContent !== lastContentRef.current) {
                lastContentRef.current = newContent;
                onChange(newContent);
            }
        });

        return removeListener;
    }, [editor, onChange]);

    return null;
}

UserInputChangePlugin.propTypes = {
    onChange: PropTypes.func.isRequired
};

/**
 * ControlledLexicalEditor - A Lexical editor that properly handles external content updates.
 *
 * Key features:
 * 1. Uses contentVersion to track when external content changes vs internal edits
 * 2. Properly updates editor state when content prop changes (e.g., section switch)
 * 3. Only calls onChange for actual user input, not programmatic updates
 */
export default function ControlledLexicalEditor({
    content,
    contentVersion,
    onChange,
    isReadOnly = false,
    placeholder = 'Enter your notes here...',
    minHeight = '300px'
}) {
    const editorConfig = {
        namespace: 'StrategyEditor',
        nodes: [ListNode, ListItemNode, OverflowNode, MarkNode],
        editable: !isReadOnly,
        onError(error) {
            console.error('[ControlledLexicalEditor] Error:', error);
        },
        theme
    };

    return (
        <Card className="controlled-lexical-editor">
            <LexicalComposer initialConfig={editorConfig}>
                <div className="editor-container" style={{ minHeight }}>
                    <ToolbarPlugin isReadOnly={isReadOnly} showMinimalToolbar={true} />
                    <div className="editor-inner">
                        <RichTextPlugin
                            contentEditable={
                                <ContentEditable
                                    className="editor-input"
                                    aria-placeholder={placeholder}
                                    placeholder={
                                        <div className="editor-placeholder">{placeholder}</div>
                                    }
                                />
                            }
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <ContentSyncPlugin content={content} contentVersion={contentVersion} />
                        <UserInputChangePlugin onChange={onChange} />
                        <ListPlugin />
                        <CheckListPlugin />
                        <HistoryPlugin />
                    </div>
                </div>
            </LexicalComposer>
        </Card>
    );
}

ControlledLexicalEditor.propTypes = {
    content: PropTypes.string,
    contentVersion: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    minHeight: PropTypes.string
};
